// A RICH-TEXT (WYSIWYG) HTML EGYETLEN, KÖZÖS POLICY-JA — tagek, attribútumok, engedett CSS-tulajdonságok.
//
// MIÉRT ITT, ÉS MIÉRT EGY HELYEN: a tárolt rich-text HTML-t HÁROM szanitáló szűrte, egymástól függetlenül —
// a web DOMPurify-ja (`lib/sanitize-html.ts`), a BFF közös `sanitizeRichHtml`-je és a messages-modul saját,
// szűkebb `sanitizeMessageBody`-ja. Mérve, a három lista MÁR ELCSÚSZOTT:
//   • a WEB-listából hiányzott az `u`, `s`, `h2`, `blockquote`, `code`, `pre` → a szerkesztőben létrehozott
//     áthúzás/idézet/kód NÉMÁN eltűnt a megjelenítéskor (a felhasználó formázott, a nézet nem mutatta),
//   • a messages-listából hiányzott az `img` és a `h4`.
// Egy szerkesztő-bővítés (aláhúzás, kiemelés, szín, igazítás) így HÁROM helyen igényelt volna kézi
// szinkronizálást — ezért a policy DATA lett, a szanitálók pedig ebből építik a konfigurációjukat.
//
// A BIZTONSÁGI ELV VÁLTOZATLAN: allowlist (deny-by-default). Ami itt nincs felsorolva, az kiesik — beleértve a
// `script`/`style` elemet, az esemény-attribútumokat és minden nem felsorolt CSS-tulajdonságot. A megjelenítés
// SOHA nem nyers HTML-ből megy (web: DOMPurify a `dangerouslySetInnerHTML` előtt; BFF: szanitálás az írás előtt).
/**
 * Engedett elemek. A készlet a TipTap-szerkesztő kimenetét fedi (StarterKit + Underline + Highlight + TextAlign
 * + TextStyle/Color/FontSize + Link + Image) ÉS a migrált legacy WYSIWYG-HTML formázó tagjeit.
 *
 * A `h1` SZÁNDÉKOSAN nincs benne: a lap-címet a felület adja, egy törzsbeli `h1` a dokumentum-hierarchiát
 * törné meg (a szerkesztő is h2-től kínál címsort).
 */
export const RICH_TEXT_TAGS = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'mark',
    'span',
    'ul',
    'ol',
    'li',
    'a',
    'h2',
    'h3',
    'h4',
    'blockquote',
    'code',
    'pre',
    'hr',
    'img',
    /**
     * VIDEÓ — rövid képernyőfelvételek beszúrása a szerkesztőből (user-kérés).
     *
     * A `video` engedélyezése SZŰKEBB, mint amilyennek látszik: a `src`-nek a SAJÁT, stabil kép-utunkra kell
     * illeszkednie (`EDITOR_IMAGE_SRC_RE`) — pont úgy, ahogy az `img`-nél —, tehát külső videó-URL, `blob:`
     * vagy `data:` forrás NEM juthat be. A `<source>` gyerek-elem SZÁNDÉKOSAN NINCS engedve: egy `src`
     * attribútum elég, és a `<source>` egy második, külön ellenőrizendő URL-felület lenne.
     *
     * `iframe` / `embed` / `object` TOVÁBBRA IS TILOS: a beágyazott idegen lejátszó (YouTube stb.) harmadik
     * felet és scriptet hozna a tárolt tartalomba.
     */
    'video',
];
/**
 * Engedett attribútumok elemenként. A `style` CSAK a `RICH_TEXT_STYLE_PROPS` tulajdonságaival élhet túl (a
 * szanitálók property-szinten szűrnek — nem a nyers string megy át).
 */
export const RICH_TEXT_ATTRS = {
    a: ['href', 'target', 'rel'],
    img: ['src', 'alt'],
    /**
     * A videó attribútumai: `src` (a saját, stabil utunk) + a LEJÁTSZÓ-VEZÉRLŐK. A `controls` nélkül a
     * felhasználó nem tudná elindítani; a `preload="metadata"` csak a hosszt/első képet kéri le, nem a teljes
     * fájlt (egy szálon több felvétel különben megaanként töltene a háttérben).
     *
     * A `title` a FÁJLNÉV hordozója: az `alt` nem érvényes attribútum a `<video>`-n, a nagyító fejlécének és a
     * tooltipnek viszont kell egy megnevezés — enélkül a felugró „melléklet"-et írt a felvétel neve helyett.
     *
     * AMI SZÁNDÉKOSAN NINCS: `autoplay` (váratlan hang), `loop`, `poster` (külön URL-felület lenne).
     */
    video: ['src', 'controls', 'preload', 'title'],
    p: ['style'],
    h2: ['style'],
    h3: ['style'],
    h4: ['style'],
    span: ['style'],
    // A TipTap Highlight `data-color`-t is írhat; a színt mi egyetlen kiemelés-tónusra kötjük, tehát nem kell.
    mark: [],
};
/**
 * Engedett CSS-tulajdonságok és az ELFOGADOTT ÉRTÉK-ALAKOK. Szándékosan nagyon szűk: a szöveg-igazítás, a
 * szöveg-szín és a betűméret az, amit a szerkesztő kínál — semmilyen pozicionálás, méret, `url()` vagy
 * `background-image` nem engedett (azok layout-törést és adat-szivárgást is lehetővé tennének).
 *
 * A `color` értéke HEX vagy `rgb()`; a `font-size` CSAK `px`, felső korláttal (a szerkesztő fix listát ad).
 */
export const RICH_TEXT_STYLE_PROPS = {
    'text-align': [/^(?:left|right|center|justify)$/],
    color: [/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i],
    'font-size': [/^(?:1[0-9]|[2-9][0-9])px$/],
};
/** A `href` engedett sémái (a `javascript:`/`data:` kiesik). */
export const RICH_TEXT_URI_SCHEMES = ['http', 'https', 'mailto', 'tel'];
/**
 * A BEÁGYAZOTT KÉP egyetlen elfogadott `src`-alakja: a BFF saját, STABIL útja. A megjelenítéskor ez oldódik
 * fel friss presigned URL-re — a tárolt HTML-be SOHA nem kerül lejáró URL, és külső `src` sem (az klikk nélküli
 * kérést indítana az idegen hosztra: tracking-pixel, IP-/referrer-szivárgás).
 */
export const EDITOR_IMAGE_SRC_RE = /^\/editor-images\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/content$/i;
/**
 * A SZERKESZTŐ betűméret-listája (px) — a legacy méret-választójának párja, de FIX készlet (nem szabad szám),
 * hogy a `font-size` szanitáló-regexe szűk maradhasson, és a tipográfia se boruljon fel.
 */
export const RICH_TEXT_FONT_SIZES = ['12px', '14px', '16px', '18px', '24px', '32px'];
/**
 * A SZERKESZTŐ szín-palettája (a MORGENS-tokenek konkrét hex-értékei + semleges tónusok). Fix készlet: szabad
 * színválasztó helyett paletta, hogy a tartalom ne tudjon olvashatatlan (pl. háttérrel egybeolvadó) színt kapni.
 * Az `undefined` érték = „nincs szín" (a szín-mark levétele).
 */
export const RICH_TEXT_COLORS = [
    '#e11d48',
    '#ea580c',
    '#ca8a04',
    '#16a34a',
    '#0891b2',
    '#2563eb',
    '#7c3aed',
    '#64748b',
];
//# sourceMappingURL=rich-text-policy.js.map